<script lang="ts">
  import { icon } from '#app/lib/icons'
  import { onChange } from '#app/lib/svelte/svelte'
  import { objectKeys, objectValues } from '#app/lib/utils'
  import type { EntitiesSortFunction } from '#app/types/entity'
  import SelectDropdown, { type SelectOption } from '#components/select_dropdown.svelte'
  import { sortEntities } from '#entities/components/lib/sort_entities_by'
  import { getSortingOptionsByName, type SortableEntityType } from '#entities/components/lib/sub_entities_browser_helpers'
  import type { SerializedEntity } from '#entities/lib/entities'
  import type { SerializedItemWithUserData } from '#inventory/lib/items'
  import type { EntityType } from '#server/types/entity'
  import { i18n } from '#user/lib/i18n'

  let {
    sortingType = 'work',
    entitiesSortFunction = $bindable(),
    waitingForItems,
    entitiesCount,
  }: {
    sortingType?: SortableEntityType
    entitiesSortFunction: EntitiesSortFunction
    waitingForItems?: Promise<SerializedItemWithUserData[]>
    entitiesCount: number
  } = $props()

  const optionsByName = getSortingOptionsByName(sortingType) || {}
  let options: SelectOption[] = $state()
  let sortingName: EntityType = $state()
  let reverseOrder = $state(false)

  if (optionsByName) {
    options = objectValues(optionsByName)
    sortingName = objectKeys(optionsByName)?.[0]
  }

  const option = $derived(optionsByName[sortingName])

  function setEntitiesSortFunction () {
    entitiesSortFunction = async (entities: SerializedEntity[]) => {
      entities = await sortEntities({
        option,
        entities,
        promise: waitingForItems,
      })
      if (reverseOrder) return entities.reverse()
      else return entities
    }
  }

  // Trigger early as parent components might depend on entitiesSortFunction to be defined
  $effect.pre(() => {
    onChange(sortingName, reverseOrder, setEntitiesSortFunction)
  })
</script>

{#if entitiesCount > 1 && options.length > 1}
  <div class="sort-selector">
    <SelectDropdown
      bind:value={sortingName}
      {options}
      buttonLabel={i18n('Sort by')}
      onSelectSameOption={() => reverseOrder = !reverseOrder}
    >
      {#snippet selectedOptionLineEnd()}
        <div class="selected-option-line-end">
          {@html icon('exchange')}
        </div>
      {/snippet}
    </SelectDropdown>
  </div>
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .sort-selector{
    align-self: end;
    :global(.select-dropdown){
      @include display-flex(row, center, stretch);
    }
    :global(.dropdown-content), :global(.dropdown-button){
      min-width: 11em;
    }
    :global(.inner-select-option){
      line-height: 1em;
    }
    :global(label){
      margin-inline-end: 1em;
    }
  }
  .selected-option-line-end{
    :global(.fa-exchange){
      transform: rotate(90deg);
      margin-inline-end: 0;
      opacity: 0.8;
      line-height: 1rem;
    }
  }
</style>
